// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useRequisicao.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requisicaoService } from '../services/requisicaoService';
import type { ListaFiltros } from '../services/listaFiltros';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarRequisicaoRequest, AtualizarRequisicaoRequest } from '../types';

interface UseRequisicaoParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período e ordem — aplicados no servidor. */
  filtros?: ListaFiltros;
}

export const useRequisicao = ({ pagina = 1, tamanhoPagina = 20, filtros = {} }: UseRequisicaoParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['requisicao', pagina, tamanhoPagina, filtros],
    queryFn: () => requisicaoService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const requisicoes  = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumo       = paginado?.resumo;

  const createMutation = useMutation({
    mutationFn: (request: CriarRequisicaoRequest) => requisicaoService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao'] });
      toast.success('Requisição criada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarRequisicaoRequest }) =>
      requisicaoService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao'] });
      toast.success('Requisição atualizada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => requisicaoService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicao'] });
      toast.success('Requisição excluída com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    requisicoes,
    total,
    totalPaginas,
    resumo,
    isLoading,
    isFetching,
    createRequisicao: createMutation.mutate,
    updateRequisicao: updateMutation.mutate,
    deleteRequisicao: deleteMutation.mutate,
    isCreating:       createMutation.isPending,
  };
};
